import React, { useEffect, useMemo, useState } from "react";
import { X, Download, Share, Plus, Home } from "lucide-react";
import {
  usePwaVisitTracker,
  isStandalone,
  isIOS,
  dismissPwaPrompt,
  isDismissedRecently,
} from "../lib/pwa";

const VISITS_THRESHOLD = 1;

/**
 * Install SyncBook to home-screen prompt.
 * Shows a non-blocking, dismissable banner starting on the 1st authenticated session.
 * Uses `beforeinstallprompt` on Chromium/desktop; on iOS Safari, shows the manual
 * "Share → Add to Home Screen" instructions modal.
 */
export default function PwaInstallPrompt() {
  const visitCount = usePwaVisitTracker();
  const [deferredEvt, setDeferredEvt] = useState(null);
  const [showBanner, setShowBanner] = useState(false);
  const [showIosModal, setShowIosModal] = useState(false);
  const alreadyInstalled = useMemo(() => isStandalone(), []);
  const ios = useMemo(() => isIOS(), []);

  useEffect(() => {
    const onBIP = (e) => {
      // Stash the event so it can be triggered later on a user gesture
      e.preventDefault();
      setDeferredEvt(e);
    };
    window.addEventListener("beforeinstallprompt", onBIP);
    const onInstalled = () => {
      setShowBanner(false);
      setDeferredEvt(null);
    };
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onBIP);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  useEffect(() => {
    if (alreadyInstalled) return;
    if (isDismissedRecently()) return;
    if (visitCount < VISITS_THRESHOLD) return;
    // For non-iOS browsers we wait for beforeinstallprompt; for iOS we just show the banner.
    if (ios) {
      setShowBanner(true);
    } else if (deferredEvt) {
      setShowBanner(true);
    }
  }, [visitCount, alreadyInstalled, ios, deferredEvt]);

  const handleInstall = async () => {
    if (ios) {
      setShowIosModal(true);
      return;
    }
    if (!deferredEvt) return;
    try {
      deferredEvt.prompt();
      const { outcome } = await deferredEvt.userChoice;
      if (outcome === "accepted" || outcome === "dismissed") {
        setDeferredEvt(null);
        setShowBanner(false);
        if (outcome === "dismissed") dismissPwaPrompt();
      }
    } catch (e) {
      setShowBanner(false);
    }
  };

  const handleDismiss = () => {
    setShowBanner(false);
    dismissPwaPrompt();
  };

  if (alreadyInstalled || !showBanner) {
    return showIosModal ? (
      <IosInstallModal onClose={() => setShowIosModal(false)} />
    ) : null;
  }

  return (
    <>
      <div
        data-testid="pwa-install-banner"
        className="fixed left-1/2 -translate-x-1/2 z-50 w-[calc(100%-24px)] max-w-md"
        style={{ bottom: "calc(env(safe-area-inset-bottom, 0px) + 88px)" }}
      >
        <div
          className="nb-card p-3 flex items-center gap-3"
          style={{ background: "#fdecc8" }}
        >
          <div
            className="w-10 h-10 shrink-0 rounded-lg border-2 border-[#1a1a1a] flex items-center justify-center"
            style={{ background: "#b5e4ca" }}
          >
            <Home size={20} strokeWidth={3} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-display font-black text-sm leading-tight">
              Add SyncBook to your Home Screen
            </div>
            <div className="text-[11px] font-medium text-[#4a4a4a] leading-snug">
              One-tap access {ios ? "+ unlocks iOS push reminders" : "+ works offline"}
            </div>
          </div>
          <button
            data-testid="pwa-install-btn"
            onClick={handleInstall}
            className="nb-btn nb-btn-dark !py-2 !px-3 !text-xs shrink-0"
          >
            <Download size={14} strokeWidth={3} /> Install
          </button>
          <button
            data-testid="pwa-dismiss-btn"
            onClick={handleDismiss}
            aria-label="Dismiss install prompt"
            className="w-8 h-8 shrink-0 rounded-lg border-2 border-[#1a1a1a] flex items-center justify-center bg-white hover:bg-[#f4f0eb]"
          >
            <X size={14} strokeWidth={3} />
          </button>
        </div>
      </div>
      {showIosModal && <IosInstallModal onClose={() => setShowIosModal(false)} />}
    </>
  );
}

function IosInstallModal({ onClose }) {
  return (
    <div
      data-testid="pwa-ios-modal"
      className="fixed inset-0 z-[60] bg-black/40 flex items-end sm:items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="nb-card-lg w-full max-w-md p-5 space-y-3"
        onClick={(e) => e.stopPropagation()}
        style={{ background: "#fff" }}
      >
        <div className="flex items-center justify-between">
          <div className="font-display font-black text-xl">Add to Home Screen</div>
          <button
            data-testid="pwa-ios-close-btn"
            onClick={onClose}
            aria-label="Close"
            className="w-9 h-9 rounded-lg border-2 border-[#1a1a1a] flex items-center justify-center bg-white"
          >
            <X size={16} strokeWidth={3} />
          </button>
        </div>
        <div className="text-sm font-medium text-[#4a4a4a]">
          Install SyncBook on your iPhone or iPad in two taps.
        </div>
        <ol className="space-y-3">
          <li className="flex items-start gap-3">
            <span
              className="w-7 h-7 shrink-0 rounded-full border-2 border-[#1a1a1a] flex items-center justify-center font-display font-black text-sm"
              style={{ background: "#b5e4ca" }}
            >
              1
            </span>
            <div className="text-sm font-medium">
              Tap the <b>Share</b> button
              <span className="inline-flex items-center gap-1 mx-1 px-1.5 py-0.5 rounded border-2 border-[#1a1a1a] bg-white">
                <Share size={12} strokeWidth={3} />
              </span>
              in Safari&apos;s toolbar.
            </div>
          </li>
          <li className="flex items-start gap-3">
            <span
              className="w-7 h-7 shrink-0 rounded-full border-2 border-[#1a1a1a] flex items-center justify-center font-display font-black text-sm"
              style={{ background: "#b5e4ca" }}
            >
              2
            </span>
            <div className="text-sm font-medium">
              Choose <b>Add to Home Screen</b>
              <span className="inline-flex items-center gap-1 mx-1 px-1.5 py-0.5 rounded border-2 border-[#1a1a1a] bg-white">
                <Plus size={12} strokeWidth={3} />
              </span>
              and confirm.
            </div>
          </li>
        </ol>
        <div className="text-[11px] font-medium text-[#4a4a4a] pt-2 border-t-2 border-[#1a1a1a]/15">
          Once installed, weekly Money Health push notifications will work on iOS too.
        </div>
      </div>
    </div>
  );
}
